import dayjs from 'dayjs';
import { useState } from 'react';
import { CiEdit } from 'react-icons/ci';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { MdDeleteForever } from 'react-icons/md';
import { CheckinHistoryIcon } from '../../../../../../assets/svg/checkin-history';
import { Card } from '../../../../../../components/card/card';
import { Chip } from '../../../../../../components/chip/chip';
import { IconButton } from '../../../../../../components/icon-button/icon-button';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { Table } from '../../../../../../features/table/table';
import { TableFilterCheckbox } from '../../../../../../features/table/table-filter/table-filter-checkbox/table-filter-checkbox';
import { TableFilterDateRange } from '../../../../../../features/table/table-filter/table-filter-date-range/table-filter-date-range';
import { TableFilterInput } from '../../../../../../features/table/table-filter/table-filter-input/table-filter-input';
import { TableFilterMultiSelect } from '../../../../../../features/table/table-filter/table-filter-multi-select/table-filter-multi-select';
import { formatRuDateString } from '../../../../../../helpers/date-time';
import { useLang } from '../../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { useBranchesStore } from '../../../../../../store/use-branch-store';
import {
  getDelay,
  getOvertime,
  useCheckInCheckOutStore,
} from '../../../../../../store/use-check-in-check-out-store';
import { useTagStaffStore } from '../../../../../../store/use-tag-staff-store';
import { getDatesDiff } from '../check-in-check-out-helpers';
import s from './check-in-check-out-table.module.css';

export const CheckInCheckOutTable = () => {
  const { t, getLocale } = useLang();
  const { addQueryParams } = useQueryParams();
  const { checkins, isLoadingFetch, pagination } = useCheckInCheckOutStore();
  const { place } = useBranchesStore();
  const [expandedRowIds, setExpandedRowIds] = useState<(string | number)[]>([]);
  const { tags } = useTagStaffStore();

  const toggleExpandRow = (id: string | number) => {
    setExpandedRowIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const editCheckInCheckOutModal = useModalParams('editCheckInCheckOut');
  const checkinHistoryModal = useModalParams('checkinHistory');

  return (
    <Card>
      <Table
        id="check-in-check-out"
        pagination={pagination}
        columnSettings
        isLoading={isLoadingFetch}
        className={s.table}
        header={{
          className: s.tableHeader,
          children: (
            <IconButton
              tooltip={t('История изменений')}
              variant="ghost"
              className="hide__M hide__S"
              onClick={() => editCheckInCheckOutModal.handleOpenModal()}
            >
              <CheckinHistoryIcon />
            </IconButton>
          ),
        }}
        expandedRowIds={expandedRowIds}
        cols={{
          employee: {
            label: t('Сотрудник'),
            width: 200,
            filter: <TableFilterInput name="employee" />,
          },
          tags: {
            label: t('Должности'),
            width: 200,
            filter: <TableFilterMultiSelect name="tags" options={tags} />,
          },
          branches: {
            label: t('Филиал'),
            width: 200,
            filter: (
              <TableFilterMultiSelect
                name="branches"
                options={(place || []).map((item) => ({
                  id: item.id.toString(),
                  value: getLocale(item.name),
                }))}
              />
            ),
          },
          date: {
            label: t('Дата'),
            width: 160,
            filter: <TableFilterDateRange name="date" />,
          },
          checkIn: {
            label: t('Приход'),
            width: 160,
            filter: (
              <TableFilterCheckbox
                name="open-shifts"
                removeParams={['closed-shifts']}
                label={t('Только открытые смены')}
              />
            ),
          },
          checkOut: {
            label: t('Уход'),
            width: 160,
            filter: (
              <TableFilterCheckbox
                name="closed-shifts"
                removeParams={['open-shifts']}
                label={t('Только закрытые смены')}
              />
            ),
          },
          hours: {
            label: t('Факт. время'),
            width: 160,
          },
          delays: {
            label: t('Опоздания'),
            width: 200,
            filter: <TableFilterCheckbox name="delay" label={t('Только c опозданиями')} />,
          },
          overtime: {
            label: t('Переработка'),
            width: 200,
            filter: <TableFilterCheckbox name="overtime" label={t('Только c переработками')} />,
          },
        }}
        rows={checkins?.map((item) => {
          const delay = getDelay(item.shift?.start_time, item.start_time) || 0;
          const overtime = getOvertime(item.end_time, item.shift?.end_time) || 0;
          const employee = item.staff;

          return {
            rowId: item.id,
            employee: `${getLocale(employee.name)} ${getLocale(employee.surname)}`,
            tags: (
              <ul className={s.chips}>
                {employee?.tags?.map((tag) => (
                  <Chip variant="info" size="md" key={tag.id}>
                    {tag.value}
                  </Chip>
                ))}
              </ul>
            ),
            branches: (
              <ul className={s.chips}>
                {place
                  ?.filter((branch) => employee.places?.some((item) => item.id === branch.id))
                  .map((branch) => (
                    <Chip variant="info" size="md" key={branch.id}>
                      {getLocale(branch.name)}
                    </Chip>
                  ))}
              </ul>
            ),
            date: item.start_time ? formatRuDateString(item.start_time) : '-',
            checkIn: item.start_time ? dayjs(item.start_time).format('HH:mm') : '-',
            checkOut: item.end_time ? dayjs(item.end_time).format('HH:mm') : '-',
            hours: getDatesDiff(item.start_time, item.end_time) || '-',
            delays: delay > 0 ? `${delay} ${t('мин')}` : '-',
            overtime: (
              <div className={s.overtime}>
                {overtime > 0 ? `${overtime} ${t('мин')}` : '-'}

                <div className={s.actions}>
                  <IconButton variant="ghost" onClick={() => toggleExpandRow(item.id)}>
                    {expandedRowIds.includes(item.id) ? <IoChevronDown /> : <IoChevronUp />}
                  </IconButton>
                </div>
              </div>
            ),
          };
        })}
        renderExpandedRowContent={(row) => {
          const item = checkins?.find((item) => item.id === row.rowId);
          return (
            <Table
              className={s.expandedTable}
              renderHead={false}
              cols={{
                details: { label: 'asd', width: 200 },
                empty1: { width: 160 },
                empty2: { width: 200 },
                checkIn: { width: 160 },
                checkOut: { width: 160 },
                empty3: { width: 160 },
                empty4: { width: 200 },
                delete: { width: 200 },
              }}
              rows={item?.checkins?.map((itm, index) => ({
                details: index === 0 ? t('Детализация') + ':' : '',
                empty1: '',
                empty2: '',
                checkIn: itm.start_time ? dayjs(itm.start_time).format('HH:mm') : '-',
                checkOut: itm.end_time ? dayjs(itm.end_time).format('HH:mm') : '-',
                empty3: '',
                empty4: '',
                delete: (
                  <HasAccess service="staff" permissions={['write']}>
                    <div className={s.actions}>
                      <IconButton
                        tooltip={t('Редактировать')}
                        variant="ghost"
                        onClick={() =>
                          checkinHistoryModal.handleOpenModal({
                            id: itm.id.toString(),
                          })
                        }
                      >
                        <CiEdit />
                      </IconButton>

                      <IconButton
                        tooltip={t('Удалить')}
                        className={s.delete}
                        variant="ghost"
                        onClick={() => {
                          addQueryParams({
                            deleteId: itm.id.toString(),
                          });
                        }}
                      >
                        <MdDeleteForever />
                      </IconButton>
                    </div>
                  </HasAccess>
                ),
              }))}
            />
          );
        }}
      />
    </Card>
  );
};
